import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isSafeNextPath, safeNextPath } from '@mindkeep/shared'
import { useAuth } from '@/features/auth/auth-provider'
import { ApiError } from '@/lib/api-client'
import { acceptInvite, acceptInviteToast, clearInviteToken, readInviteToken, saveInviteToken } from '@/lib/invite'
import { env } from '@/lib/env'
import { supabase } from '@/lib/supabase'

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // M8.2: Einladungs-Kontext (URL-Param oder gemerkter Token aus lib/invite).
  const [inviteToken, setInviteToken] = useState<string | null>(() => params.get('invite') ?? readInviteToken())
  const [inviteAcceptFailed, setInviteAcceptFailed] = useState(false)

  useEffect(() => {
    const fromUrl = params.get('invite')
    if (fromUrl) {
      saveInviteToken(fromUrl)
      setInviteToken(fromUrl)
    }
  }, [params])

  // Bei einem fehlgeschlagenen Einladungs-Accept (z.B. falsches Konto) bleibt
  // die Seite offen, damit ein Wechsel zum richtigen Konto moeglich ist.
  if (!isLoading && isAuthenticated && !mfaFactorId && !inviteAcceptFailed) {
    return <Navigate to={safeNextPath(params.get('next'))} replace />
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setSubmitting(true)
    // M8.2: Mit Einladung reicht der Backend-Endpunkt den Token in der
    // Magic-Link-URL weiter (/auth/verify?invite=…) zum automatischen Accept.
    try {
      // W11: The MCP consent target survives the magic link round trip.
      const next = params.get('next')
      const body: { email: string; invite?: string; next?: string } = { email }
      if (inviteToken) body.invite = inviteToken
      if (isSafeNextPath(next)) body.next = next
      const response = await fetch(`${env.VITE_API_URL.replace(/\/+$/, '')}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(json?.error?.message ?? 'Magic Link konnte nicht gesendet werden.')
      }
      toast.success('Magic Link gesendet — bitte E-Mail prüfen.')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Magic Link konnte nicht gesendet werden.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePassword(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    setSubmitting(true)
    setInviteAcceptFailed(false)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setSubmitting(false)
      toast.error(error.message)
      return
    }

    const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (assurance?.nextLevel === 'aal2' && assurance.currentLevel !== 'aal2') {
      const { data: factors, error: factorError } = await supabase.auth.mfa.listFactors()
      const factorId = factors?.totp?.find((factor) => factor.status === 'verified')?.id
      if (factorError || !factorId) {
        toast.error(factorError?.message ?? 'MFA-Faktor nicht gefunden.')
      } else {
        setMfaFactorId(factorId)
      }
      setSubmitting(false)
      return
    }

    // M8.2: Ohne MFA-Pflicht wird eine vorhandene Einladung direkt angenommen.
    // (Mit MFA bleibt der Token gemerkt und wird nach der MFA-Bestaetigung
    // ueber /auth/verify bzw. die Einladungs-Seite eingeloest.)
    if (inviteToken) {
      try {
        const result = await acceptInvite(inviteToken)
        clearInviteToken()
        toast.success(acceptInviteToast(result.status))
        setSubmitting(false)
        navigate('/', { replace: true })
        return
      } catch (acceptErr) {
        setSubmitting(false)
        setInviteAcceptFailed(true)
        if (acceptErr instanceof ApiError && acceptErr.code === 'email_mismatch') {
          toast.error('Diese Einladung gehört zu einer anderen E-Mail-Adresse. Melde dich mit der eingeladenen E-Mail-Adresse an.')
        } else {
          toast.error(acceptErr instanceof Error ? acceptErr.message : 'Einladung konnte nicht angenommen werden.')
        }
        return
      }
    }
    setSubmitting(false)
  }

  async function handleMfa(e: React.FormEvent) {
    e.preventDefault()
    if (!mfaFactorId || mfaCode.trim().length < 6) return
    setSubmitting(true)
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: mfaFactorId,
    })
    if (challengeError || !challenge) {
      setSubmitting(false)
      toast.error(challengeError?.message ?? 'MFA-Challenge konnte nicht gestartet werden.')
      return
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: challenge.id,
      code: mfaCode.trim(),
    })
    setSubmitting(false)
    if (error) {
      toast.error(error.message)
    } else {
      setMfaFactorId(null)
      setMfaCode('')
      toast.success('MFA bestätigt.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 text-center">
          <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">MindKeep</h1>
          <p className="mt-1 text-sm text-muted-foreground">KI-Wissensbasis für dein Unternehmen</p>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle>Anmelden</CardTitle>
            <CardDescription>Wähle deine Anmeldemethode.</CardDescription>
          </CardHeader>
          <CardContent>
            {inviteToken && !mfaFactorId ? (
              <p className="mb-4 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                Nach der Anmeldung nimmst du die Einladung automatisch an.
              </p>
            ) : null}
            {mfaFactorId ? (
              <form onSubmit={handleMfa} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Zwei-Faktor-Code</Label>
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting || mfaCode.trim().length < 6}>
                  {submitting ? 'Wird geprüft…' : 'Code bestätigen'}
                </Button>
              </form>
            ) : (
            <Tabs defaultValue="magic-link">
              <TabsList className="w-full mb-6">
                <TabsTrigger value="magic-link" className="flex-1">
                  Magic Link
                </TabsTrigger>
                <TabsTrigger value="password" className="flex-1">
                  E-Mail + Passwort
                </TabsTrigger>
              </TabsList>

              <TabsContent value="magic-link">
                <form onSubmit={handleMagicLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ml-email">E-Mail</Label>
                    <Input
                      id="ml-email"
                      type="email"
                      placeholder="name@beispiel.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting || !email}
                  >
                    {submitting ? 'Wird gesendet…' : 'Magic Link senden'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="password">
                <form onSubmit={handlePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pw-email">E-Mail</Label>
                    <Input
                      id="pw-email"
                      type="email"
                      placeholder="name@beispiel.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pw-password">Passwort</Label>
                    <Input
                      id="pw-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting || !email || !password}
                  >
                    {submitting ? 'Wird angemeldet…' : 'Anmelden'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Noch kein Workspace? <Link className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/auth/signup">Kostenlos starten</Link>
        </p>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/legal/privacy">Datenschutzerklärung</Link>
          {' · '}
          <Link className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/legal/terms">Nutzungsbedingungen</Link>
          {' · '}
          <Link className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/legal/impressum">Impressum</Link>
        </p>
      </div>
    </div>
  )
}
